export interface ITokenUsage {
    inputTokens: number;
    outputTokens: number;
}

export interface ICanvas {
    title?: string;
    key?: string;
    url?: string;
}

export interface IConfig {
    navbarOpen: boolean;
    canvasOpen: boolean;
    canvas?: ICanvas;
    selectedModel: string;
}

// Default model ID inline to avoid circular dependency with models.ts
export const defaultIConfig: IConfig = {
    navbarOpen: true,
    canvasOpen: false,
    canvas: undefined,
    selectedModel: "us.anthropic.claude-sonnet-5",
};

export interface IUser {
    userid: string;
    name: string;
    email: string;
    department?: string;
}

export interface IChat {
    chatid: string;
    projectid?: string;
    user: string;
    title?: string;
    favorite: boolean;
    shareid?: string;
    messages?: IMessage[];
    created: Date;
    lastmessage: Date;
}

export interface IShare {
    shareid: string;
    chatid: string;
    email: string;
    messages?: IMessage[];
    created: Date;
}

export interface IProject {
    projectid: string;
    user: string;
    title?: string;
    favorite: boolean;
    description?: string;
    instructions?: string;
    files?: IS3Location[]; // S3 URIs for project files (images and documents)
    chats: IChat[];
    created: Date;
}

export type TRole = "user" | "assistant";

// https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_Message.html
export interface IMessage {
    messageid: string;
    role: TRole;
    content: IContentBlock[];
    stopped?: boolean; // True when the user stopped this assistant turn mid-stream
}

// Request interface for the chat API
export interface IChatRequest {
    chatid: string;
    projectid?: string;
    message?: IMessage;
    retry_messageidx?: number;
    model?: string;
}

export type TDocumentFormat = "pdf" | "csv" | "doc" | "docx" | "xls" | "xlsx" | "html" | "txt" | "md";

// https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_S3Location.html
export interface IS3Location {
    uri: string; // The S3 location URI
}

// https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_DocumentSource.html
export interface IDocumentSource {
    bytes?: string; // Base64-encoded binary data object
    s3Location?: IS3Location; // The S3 location of where the document is stored
}

// https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_DocumentBlock.html
// Each document's size must be no more than 4.5 MB.
export interface IDocumentBlock {
    format: TDocumentFormat;
    name: string;
    source: IDocumentSource;
}

export type TImageFormat = "png" | "jpeg" | "gif" | "webp";

// https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_ImageSource.html
export interface IImageSource {
    bytes?: string; // Base64-encoded binary data object
    s3Location?: IS3Location; // The S3 location of where the image is stored
    thumbnail?: string; // Base64 data URL for 60x60 thumbnail
}

// https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_ImageBlock.html
// Each image's size, height, and width must be no more than 3.75 MB, 8000 px, and 8000 px, respectively.
export interface IImageBlock {
    format: TImageFormat;
    source: IImageSource;
}

// https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_ToolResultContentBlock.html
export interface IToolResultContentBlock {
    document?: IDocumentBlock;
    image?: IImageBlock;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    json?: any;
    text?: string;
}

export type TToolStatus = "success" | "error";

// https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_ToolResultBlock.html
export interface IToolResultBlock {
    content: IToolResultContentBlock[];
    toolUseId: string;
    status?: TToolStatus;
}

// https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_ToolUseBlock.html
export interface IToolUseBlock {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    input: any;
    name: string;
    toolUseId: string;
}

export type TContentBlockType = "text" | "image" | "document" | "tool_use" | "tool_result" | "reasoning";

// https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_ContentBlock.html
// Each image's size, height, and width must be no more than 3.75 MB, 8000 px, and 8000 px, respectively.
// Each document's size must be no more than 4.5 MB.
// If you include a ContentBlock with a document field in the array, you must also include a ContentBlock with a text field.
export interface IContentBlock {
    messageid?: string;
    contentid?: string;
    type?: TContentBlockType;
    document?: IDocumentBlock;
    // guardContent?: IGuardrailConverseContentBlock;
    reasoningContent?: ReasoningContentBlock;
    subagentBlock?: ISubagentBlock;
    image?: IImageBlock;
    text?: string;
    toolResult?: IToolResultBlock;
    toolUse?: IToolUseBlock;
    role?: TRole;
    projectfile?: boolean;
    stopped?: boolean; // Propagated from a stopped IMessage so flattened blocks can render a "stopped" marker
    // Transient fields from GraphQL — consumed by reconstructSubagentBlocks, not rendered directly
    subagentReasoning?: { agentName: string; reasoningText: string };
    subagentToolUse?: { agentName: string; toolName: string; toolInput?: any };
    subagentToolResult?: { agentName: string; status: string; contentPreview: string };
    subagentText?: { agentName: string; text: string };
    // Lifted to top-level by reconstructSubagentBlocks; rendered by ContentBlocks (not folded into ISubagentBlock)
    subagentAuthRequired?: { agentName: string; provider: string; authorizationUrl: string };
}

export interface ISubagentTool {
    toolName: string;
    toolInput?: any;
    status?: string;
    resultPreview?: string;
}

export interface ISubagentBlock {
    agentName: string;
    reasoning: string;
    tools: ISubagentTool[];
    text: string;
    isComplete: boolean;
}

export interface ReasoningContentBlock {
    reasoningText?: IReasoningTextBlock;
}

export interface IReasoningTextBlock {
    text: string;
    signature?: string;
}

export interface IPrompt {
    title: string;
    prompt: string;
    user: string;
    category: string;
}

export interface ISummary {
    summary: string;
}

export interface IRelease {
    id: string;
    title: string;
    created: Date;
    summary: string;
    items: string[];
}
